import { motion } from "framer-motion";
import { Gauge, Beaker, Mountain, Clock, Sprout, ShieldCheck } from "lucide-react";

const BENEFITS = [
  { icon: Gauge, title: "30× Faster Coverage", body: "A single drone can cover 40+ acres an hour — finish a field in a morning, not a week." },
  { icon: Beaker, title: "20–30% Less Chemical", body: "Precision nozzles and GPS-guided passes mean tighter swaths and lower drift." },
  { icon: Mountain, title: "Any Terrain, Any Crop", body: "Wet fields, slopes, dense groves, irrigated rows — terrain-following flight handles it all." },
  { icon: Clock, title: "Spray Within Hours", body: "Pest outbreak? We mobilize same-day across South Florida — no waiting on tractor availability." },
  { icon: Sprout, title: "Zero Soil Compaction", body: "Nothing touches the ground. Healthier soil structure means better root systems and yield." },
  { icon: ShieldCheck, title: "Drift-Controlled", body: "Closed-loop downwash and electrostatic spraying keep chemical on the target — and off neighbours." },
];

export default function WhyDrone() {
  return (
    <section id="why" data-testid="why-section" className="relative py-24 md:py-32 bg-muted/40 overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-sky/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-crop/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-accent mb-4">Why drone spraying</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary font-bold leading-tight">
            The new standard for crop protection in <span className="text-accent">Florida agriculture.</span>
          </h2>
        </div>

        {/* Bento grid */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            const featured = i === 0;
            return (
              <motion.div
                key={b.title}
                data-testid={`benefit-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className={`relative rounded-3xl p-7 border transition-all duration-300 ${
                  featured
                    ? "md:col-span-2 bg-primary text-primary-foreground border-primary shadow-card-hover"
                    : "bg-card text-foreground border-border shadow-card hover:shadow-card-hover hover:-translate-y-0.5"
                }`}
              >
                <div className={`h-12 w-12 rounded-2xl flex items-center justify-center mb-5 ${featured ? "bg-accent text-accent-foreground" : "bg-muted text-primary"}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className={`font-display text-xl md:text-2xl font-bold ${featured ? "" : "text-primary"}`}>{b.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${featured ? "text-primary-foreground/85" : "text-muted-foreground"}`}>{b.body}</p>

                {featured && (
                  <div className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent">
                    <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" /> Most loved
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
